import React, { useEffect, useRef, useState } from "react";
import { z } from "zod";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

import {
	Alert,
	ButtonStandard,
	Checkbox,
	FormField,
	FormHandle,
	FormInputText,
	FormTextEditor,
	Icon,
	ModalBase,
	PYForm,
	Text,
} from "@performyard/py-components";

import Tooltip from "../../../../../../components/Tooltip";

import { createEngagementCustomFactor, updateEngagementCustomFactor } from "../../../../../../api/services";

const MAX_NAME_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 500;

const stripHtmlTags = (html: string) => html.replace(/<[^>]*>/g, "");

const customFactorSchema = z.object({
	name: z
		.string()
		.min(1, "Factor name is required")
		.max(MAX_NAME_LENGTH, `Factor name must be ${MAX_NAME_LENGTH} characters or less`),
	description: z
		.string()
		.default("")
		.refine((val) => stripHtmlTags(val).length <= MAX_DESCRIPTION_LENGTH, {
			message: `Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`,
		}),
	includeInSatisfaction: z.boolean(),
});

type CustomFactorFormValues = z.infer<typeof customFactorSchema>;

export interface CustomFactorModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
	factor?: {
		_id: string;
		name: string;
		description: string | null;
		include_in_overall_satisfaction: boolean;
	} | null;
	currentOverallSatisfactionCount: number;
	maxOverallSatisfactionFactors: number;
	maxAllowedFactors: number;
}

const CustomFactorModal: React.FC<CustomFactorModalProps> = ({
	isOpen,
	onClose,
	onSuccess,
	factor,
	currentOverallSatisfactionCount,
	maxOverallSatisfactionFactors,
	maxAllowedFactors,
}) => {
	const isEditMode = !!factor;
	const formRef = useRef<FormHandle<typeof customFactorSchema>>(null);
	const [apiError, setApiError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setApiError(null);
			formRef.current?.reset();
		}
	}, [isOpen, factor]);

	// Determine if the checkbox should be disabled
	// It's disabled if we're at max AND we're not editing a factor that already has it enabled
	const isCheckboxDisabled =
		currentOverallSatisfactionCount >= maxOverallSatisfactionFactors &&
		!(isEditMode && factor?.include_in_overall_satisfaction);

	const handleSubmit = async ({
		value,
		setError,
	}: {
		value: CustomFactorFormValues;
		resetForm: () => void;
		setError: (field: keyof CustomFactorFormValues, error: string) => void;
	}) => {
		setApiError(null);

		setIsSubmitting(true);
		try {
			const payload = {
				name: value.name.trim(),
				description: value.description.trim() || null,
				include_in_overall_satisfaction: value.includeInSatisfaction,
			};

			if (isEditMode && factor) {
				await updateEngagementCustomFactor(factor._id, payload);
			} else {
				await createEngagementCustomFactor(payload);
			}

			onSuccess();
		} catch (error: any) {
			const errorMessage = error?.data?.message || "An error occurred";
			if (errorMessage.toLowerCase().includes("already exists")) {
				setError("name", "Factor name already exists");
			} else if (errorMessage.toLowerCase().includes("reserved factor name")) {
				setError("name", "Factor name already exists");
			} else {
				setApiError(errorMessage);
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const defaultValues: CustomFactorFormValues = {
		name: factor?.name || "",
		description: factor?.description || "",
		includeInSatisfaction: factor?.include_in_overall_satisfaction || false,
	};

	return (
		<ModalBase isOpen={isOpen} onClose={onClose}>
			<div className="w-[432px] px-6 pb-6">
				<Text
					size="sub-heading"
					weight="semi-bold"
					dangerouslySetClassName={`text-center ${isEditMode ? "mb-6" : "mb-2"}`}
				>
					{isEditMode ? "Edit a Custom Factor" : "Create a Custom Factor"}
				</Text>
				{!isEditMode && (
					<Text size="paragraph-2" weight="regular" dangerouslySetClassName="mb-6 text-center">
						{`Create up to ${maxAllowedFactors} custom factors to help organize your custom questions`}
					</Text>
				)}

				<PYForm
					key={factor ? `${factor._id}-${factor.include_in_overall_satisfaction}` : "create"}
					ref={formRef}
					id="custom-factor-form"
					schema={customFactorSchema}
					defaultValues={defaultValues}
					onSubmit={handleSubmit}
					validateOn={["onSubmit"]}
				>
					<div className="flex flex-col gap-4">
						{/* Factor Name */}
						<FormInputText id="name" label="Factor Name" placeholder="Factor Name" />

						<FormTextEditor
							id="description"
							label="Factor Description"
							characterLimit={MAX_DESCRIPTION_LENGTH}
							placeholder="Your description here"
							minHeight={120}
						/>

						{/* Overall Satisfaction Checkbox */}
						<FormField<boolean>
							id="includeInSatisfaction"
							input={(field) => (
								<div className="flex flex-col gap-2">
									<div className="flex items-start gap-2">
										<Checkbox
											id="include-satisfaction"
											checked={field.value}
											onCheckedChange={(checked) => field.onChange(checked as boolean)}
											disabled={isCheckboxDisabled}
											aria-label="Include in satisfaction metrics"
										/>
										<div className="flex gap-0.5">
											<label
												htmlFor="include-satisfaction"
												className={`cursor-pointer text-paragraph-3 font-medium ${
													isCheckboxDisabled ? "text-neutral-5" : "text-neutral-9"
												}`}
											>
												Include in satisfaction metrics
											</label>
											<Tooltip
												message="Include this factor in the satisfaction metrics on engagement pages. To impact satisfaction scores, assign at least one rating scale (Likert-5) custom question to this factor."
												placement="top"
											>
												<Icon icon={faCircleInfo} size={12} color="neutral-7" />
											</Tooltip>
										</div>
									</div>
									<Alert
										type="neutral"
										message={
											isCheckboxDisabled
												? "Maximum reached. Remove a custom factor from satisfaction metrics to include this one."
												: `${currentOverallSatisfactionCount}/${maxOverallSatisfactionFactors} factors included in satisfaction metrics`
										}
									/>
								</div>
							)}
						/>

						{apiError && (
							<Text size="caption" color="red-5">
								{apiError}
							</Text>
						)}

						{/* Buttons */}
						<div className="mt-2 flex justify-center gap-2">
							<div>
								<ButtonStandard variant="outline" color="neutral" size="large" onClick={onClose}>
									Cancel
								</ButtonStandard>
							</div>
							<div>
								<ButtonStandard
									variant="solid"
									color="primary"
									size="large"
									type="button"
									disabled={isSubmitting}
									onClick={async () => {
										const isValid = await formRef.current?.validate(true);
										if (isValid) {
											await formRef.current?.submit();
										}
									}}
								>
									{isEditMode ? "Save" : "Create Factor"}
								</ButtonStandard>
							</div>
						</div>
					</div>
				</PYForm>
			</div>
		</ModalBase>
	);
};

CustomFactorModal.displayName = "CustomFactorModal";

export default CustomFactorModal;
